import { useState, useRef, useEffect } from "react";
import { Mic, MicOff, Play, Pause, Square, Send, Trash2 } from "lucide-react";

interface AudioRecorderProps {
  onAudioReady: (audioBlob: Blob) => void;
  onCancel: () => void;
  isSending?: boolean;
}

export function AudioRecorder({
  onAudioReady,
  onCancel,
  isSending,
}: AudioRecorderProps) {
  const [isRecording, setIsRecording] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [recordedAudio, setRecordedAudio] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string>("");
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startTimeRef = useRef<number>(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Limpar recursos ao desmontar
  useEffect(() => {
    return () => {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [audioUrl]);

  // Iniciar gravação
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100,
        },
      });

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: "audio/webm;codecs=opus",
      });

      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(chunksRef.current, {
          type: "audio/webm;codecs=opus",
        });
        setRecordedAudio(audioBlob);

        const url = URL.createObjectURL(audioBlob);
        setAudioUrl(url);

        // Parar todas as tracks do stream
        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorder.start(1000); // Coleta dados a cada 1 segundo
      setIsRecording(true);
      startTimeRef.current = Date.now();

      // Timer para duração da gravação
      intervalRef.current = setInterval(() => {
        const elapsed = (Date.now() - startTimeRef.current) / 1000;
        setDuration(elapsed);
      }, 100);
    } catch (error) {
      console.error("Erro ao acessar microfone:", error);
      alert("Erro ao acessar o microfone. Verifique as permissões.");
    }
  };

  // Parar gravação
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);

      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }

      // Calcular duração final
      const finalDuration = (Date.now() - startTimeRef.current) / 1000;
      setDuration(finalDuration);
    }
  };

  // Reproduzir áudio gravado
  const playAudio = () => {
    if (audioRef.current) {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  // Pausar áudio
  const pauseAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };

  // Resetar gravação
  const resetRecording = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }

    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    setRecordedAudio(null);
    setAudioUrl("");
    setDuration(0);
    setCurrentTime(0);
    setIsPlaying(false);
    setIsRecording(false);
  };

  // Enviar áudio
  const sendAudio = () => {
    if (recordedAudio) {
      onAudioReady(recordedAudio);
    }
  };

  // Cancelar
  const handleCancel = () => {
    resetRecording();
    onCancel();
  };

  // Formatar tempo
  const formatTime = (time: number) => {
    // Validar se o tempo é um número válido
    if (!time || isNaN(time) || !isFinite(time)) {
      return "0:00";
    }

    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  return (
    <div className="bg-white border border-gray-300 rounded-lg p-4 shadow-lg">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-gray-700">
          {isRecording
            ? "Gravando..."
            : recordedAudio
            ? "Áudio Gravado"
            : "Gravar Áudio"}
        </h3>
        <button
          onClick={handleCancel}
          className="text-gray-400 hover:text-gray-600"
          title="Cancelar"
        >
          <Trash2 size={16} />
        </button>
      </div>

      {/* Controles principais */}
      <div className="flex items-center gap-3 mb-3">
        {!recordedAudio ? (
          // Controles de gravação
          <button
            onClick={isRecording ? stopRecording : startRecording}
            className={`p-2 rounded-full transition-colors ${
              isRecording
                ? "bg-red-500 text-white hover:bg-red-600"
                : "bg-azul-400 text-white hover:bg-azul-500"
            }`}
            title={isRecording ? "Parar gravação" : "Iniciar gravação"}
          >
            {isRecording ? <Square size={20} /> : <Mic size={20} />}
          </button>
        ) : (
          // Controles de reprodução
          <button
            onClick={isPlaying ? pauseAudio : playAudio}
            className="p-2 rounded-full bg-azul-400 text-white hover:bg-azul-500 transition-colors"
            title={isPlaying ? "Pausar" : "Reproduzir"}
          >
            {isPlaying ? <Pause size={20} /> : <Play size={20} />}
          </button>
        )}

        {/* Tempo */}
        <div className="flex-1">
          <div className="text-sm text-gray-600">
            {formatTime(isRecording ? duration : currentTime)}
            {recordedAudio && !isRecording && ` / ${formatTime(duration)}`}
          </div>

          {/* Barra de progresso para reprodução */}
          {recordedAudio && (
            <div className="w-full bg-gray-200 rounded-full h-1 mt-1">
              <div
                className="bg-azul-400 h-1 rounded-full transition-all duration-100"
                style={{
                  width: `${
                    duration > 0 ? (currentTime / duration) * 100 : 0
                  }%`,
                }}
              />
            </div>
          )}
        </div>

        {/* Botões de ação */}
        {recordedAudio && (
          <div className="flex gap-2">
            <button
              onClick={resetRecording}
              className="p-2 rounded-full bg-gray-400 text-white hover:bg-gray-500 transition-colors"
              title="Gravar novamente"
            >
              <MicOff size={16} />
            </button>
            <button
              onClick={sendAudio}
              disabled={isSending}
              className="p-2 rounded-full bg-green-500 text-white hover:bg-green-600 transition-colors"
              title="Enviar áudio"
            >
              <Send size={16} />
            </button>
          </div>
        )}
      </div>

      {/* Elemento de áudio oculto */}
      {audioUrl && (
        <audio
          ref={audioRef}
          src={audioUrl}
          onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
          onEnded={() => {
            setIsPlaying(false);
            setCurrentTime(0);
          }}
          onLoadedMetadata={(e) => {
            const audioDuration = e.currentTarget.duration;
            // Só usar a duração do áudio se for válida, senão manter a calculada
            if (
              audioDuration &&
              !isNaN(audioDuration) &&
              isFinite(audioDuration)
            ) {
              setDuration(audioDuration);
            }
          }}
        />
      )}

      {/* Indicador visual de gravação */}
      {isRecording && (
        <div className="flex items-center justify-center gap-2 text-red-500 text-xs">
          <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
          REC
        </div>
      )}
    </div>
  );
}
